import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GoBackButton, Button, ConfirmModal } from '~/components';
import { useLoading } from '~/hooks/loading';
import { TrainingStoreService } from '~/services';
import { handleYupValidationError } from '~/utils';

import EditTrainingStoreForm, {
  ITrainingStoreData,
} from '../EditTrainingStoreForm';

import * as S from './styles';

interface EditTrainingStoreParams {
  trainingStoreId: string;
}

function EditTrainingStore(): ReactElement {
  const { setLoading } = useLoading();

  const formRef = useRef<FormHandles>(null);
  const { trainingStoreId } = useParams<EditTrainingStoreParams>();
  const router = useHistory();
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false);
  const [trainingStore, setTrainingStore] = useState<ITrainingStoreData>();

  const handleEditTrainingStoreSubmit = useCallback(
    async (data: ITrainingStoreData) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        const result = await TrainingStoreService.updateTrainingStores(
          data,
          trainingStoreId,
        );
        if (result.status === 200) {
          toast.success('Training Store editado com sucesso!');
          router.goBack();
        } else {
          toast.error('Ocorreu um problema ao editar o Training Store!');
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleYupValidationError(formRef, error);
      }
    },
    [trainingStoreId, router],
  );

  const handleDeleteTrainingStore = async () => {
    try {
      setLoading(true);

      const result = await TrainingStoreService.deleteTrainingStores(
        trainingStoreId,
      );
      if (result) {
        toast.success('Training Store deletado com sucesso!');
        router.goBack();
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao deletar o Training Store!');
    }
  };

  const getTrainingStoreData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await TrainingStoreService.getTrainingStoreById(
        trainingStoreId,
      );
      setTrainingStore({
        category: data.category,
        description: data.description,
      });

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }, [trainingStoreId]);

  useEffect(() => {
    getTrainingStoreData();
  }, [getTrainingStoreData, trainingStoreId]);

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.Header>
          <S.Title>Editar {trainingStore?.description}</S.Title>
        </S.Header>
        <EditTrainingStoreForm
          formRef={formRef}
          formName="edit-method"
          handleSubmit={handleEditTrainingStoreSubmit}
        />
        <S.Footer>
          <Button
            form="edit-method"
            type="submit"
            textContent="Salvar"
            variant="black"
            size="small"
            extraStyles={{
              width: '200px',
            }}
          />
          <Button
            type="button"
            onClick={() => setConfirmDeleteModal(true)}
            textContent="Excluir"
            variant="red"
            size="small"
            extraStyles={{
              width: '200px',
            }}
          />
        </S.Footer>
      </S.Container>
      <ConfirmModal
        modalIsVisible={confirmDeleteModal}
        setModalIsVisible={setConfirmDeleteModal}
        handleSubmit={handleDeleteTrainingStore}
        textContent="Tem certeza de que deseja excluir o Training Store?"
        title="Deletar Training Store"
      />
    </>
  );
}

export default EditTrainingStore;
